"use strict";
// CSS physical border radius shared evaluator
// Eliminates code duplication across all physical border radius properties
// Supports border-top-left-radius, border-top-right-radius, border-bottom-right-radius, border-bottom-left-radius
Object.defineProperty(exports, "__esModule", { value: true });
exports.parsePhysicalBorderRadiusProperty = parsePhysicalBorderRadiusProperty;
exports.physicalBorderRadiusToCSSValue = physicalBorderRadiusToCSSValue;
const length_1 = require('./length.cjs');
const percentage_1 = require('./percentage.cjs');
const css_variable_1 = require('./css-variable.cjs');
const shared_utils_1 = require('../utils/shared-utils.cjs');
/**
 * Shared parser for all physical border radius properties
 * Handles length, percentage, CSS functions, keywords, and variables with non-negative validation
 *
 * @param value - The CSS value to parse
 * @returns Parsed value or null if invalid
 *
 * @example
 * parsePhysicalBorderRadiusProperty('10px') // { type: 'length', value: 10, unit: 'px' }
 * parsePhysicalBorderRadiusProperty('50%') // { type: 'percentage', value: 50, unit: '%' }
 * parsePhysicalBorderRadiusProperty('inherit') // { type: 'keyword', keyword: 'inherit' }
 */
function parsePhysicalBorderRadiusProperty(value) {
    if (!value || typeof value !== 'string') {
        return null;
    }
    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }
    // CSS variables
    if ((0, shared_utils_1.isCssVariable)(trimmed)) {
        return (0, css_variable_1.parse)(trimmed);
    }
    // Global keywords
    if ((0, shared_utils_1.isGlobalKeyword)(trimmed)) {
        return { type: 'keyword', keyword: trimmed.toLowerCase() };
    }
    // Try percentage first (to handle cases like '50%')
    const percentageResult = (0, percentage_1.parse)(trimmed);
    if (percentageResult && (0, shared_utils_1.isNonNegative)(percentageResult)) {
        return percentageResult;
    }
    // Try length
    const lengthResult = (0, length_1.parse)(trimmed);
    if (lengthResult && (0, shared_utils_1.isNonNegative)(lengthResult)) {
        return lengthResult;
    }
    return null;
}
/**
 * Shared toCSSValue function for all physical border radius properties
 * Converts parsed values back to CSS string representation
 *
 * @param parsed - The parsed value to convert
 * @returns CSS string or null if invalid
 *
 * @example
 * physicalBorderRadiusToCSSValue({ type: 'length', value: 10, unit: 'px' }) // '10px'
 * physicalBorderRadiusToCSSValue({ type: 'percentage', value: 50, unit: '%' }) // '50%'
 * physicalBorderRadiusToCSSValue({ type: 'keyword', keyword: 'inherit' }) // 'inherit'
 */
function physicalBorderRadiusToCSSValue(parsed) {
    if (!parsed) {
        return null;
    }
    if ('CSSvariable' in parsed) {
        return (0, css_variable_1.toCSSValue)(parsed);
    }
    if ('keyword' in parsed) {
        return parsed.keyword;
    }
    if (parsed.type === 'length') {
        return `${parsed.value}${parsed.unit}`;
    }
    if (parsed.type === 'percentage') {
        return `${parsed.value}%`;
    }
    if (parsed.type === 'function') {
        return `${parsed.function}(${parsed.expression})`;
    }
    return null;
}
